import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  Search, Flame, Pause, Layers, ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  Banknote, CreditCard, QrCode, Wallet, MapPin, Phone, FileText, Building2,
  Sparkles, Crown, TrendingUp,
} from 'lucide-react';

const fmt = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const norm = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const STORE_INFO = {
  name: 'Hit Grill',
  tagline: 'Delivery e Retirada',
  cnpj: '12.345.678/0001-90',
  address_line1: 'Av. Hildebrando de Lima, 699',
  address_line2: 'KM 18, Osasco - SP',
  cep: '06190-160',
  phone: '+55 11 98308-6866',
  phone_digits: '5511983086866',
};

const PAYMENT_METHODS = [
  { id: 'pix',      label: 'PIX',      color: '#10b981', icon: QrCode },
  { id: 'credito',  label: 'Crédito',  color: '#3b82f6', icon: CreditCard },
  { id: 'debito',   label: 'Débito',   color: '#06b6d4', icon: CreditCard },
  { id: 'dinheiro', label: 'Dinheiro', color: '#22c55e', icon: Banknote },
  { id: 'verocard', label: 'Verocard', color: '#a855f7', icon: Wallet },
  { id: 'sodexo',   label: 'Sodexo',   color: '#ef4444', icon: Wallet },
  { id: 'vr',       label: 'VR',       color: '#16a34a', icon: Wallet },
  { id: 'pluxe',    label: 'Pluxe',    color: '#f97316', icon: Wallet },
];

/* Placeholder bonito por palavra-chave da categoria/nome */
const PLACEHOLDER_EMOJIS = [
  { kw: ['hambur', 'smash', 'lanche', 'baguete'], emoji: '🍔', from: '#fde68a', to: '#fb923c' },
  { kw: ['marmita', 'feijao', 'feijão', 'arroz'], emoji: '🍱', from: '#fef3c7', to: '#f59e0b' },
  { kw: ['picanha', 'carne', 'churrasco', 'espeto', 'espetinho', 'contra', 'mignon', 'maminha', 'cupim', 'fraldinha', 'alcatra'], emoji: '🥩', from: '#fecaca', to: '#dc2626' },
  { kw: ['frango', 'sobrecoxa', 'tulipa', 'coxa'], emoji: '🍗', from: '#fed7aa', to: '#ea580c' },
  { kw: ['linguica', 'linguiça', 'salsicha', 'panceta', 'bacon', 'pork', 'porco'], emoji: '🌭', from: '#fda4af', to: '#e11d48' },
  { kw: ['queijo', 'coalho'], emoji: '🧀', from: '#fef08a', to: '#facc15' },
  { kw: ['salada', 'verdura', 'alface', 'tomate'], emoji: '🥗', from: '#bbf7d0', to: '#16a34a' },
  { kw: ['batata', 'fritas'], emoji: '🍟', from: '#fef08a', to: '#f97316' },
  { kw: ['refri', 'coca', 'guarana', 'guaraná', 'fanta', 'sprite', 'bebida'], emoji: '🥤', from: '#bae6fd', to: '#0284c7' },
  { kw: ['cerveja', 'chopp'], emoji: '🍺', from: '#fef08a', to: '#ca8a04' },
  { kw: ['suco'], emoji: '🧃', from: '#fde68a', to: '#ea580c' },
  { kw: ['agua', 'água'], emoji: '💧', from: '#bae6fd', to: '#0369a1' },
  { kw: ['caldo', 'sopa'], emoji: '🍲', from: '#fed7aa', to: '#c2410c' },
  { kw: ['adicional', 'molho', 'tempero'], emoji: '🌶️', from: '#fecaca', to: '#dc2626' },
  { kw: ['entrada', 'aperitivo', 'isca'], emoji: '🍤', from: '#fed7aa', to: '#fb923c' },
  { kw: ['sobremesa', 'doce', 'pudim'], emoji: '🍮', from: '#fde68a', to: '#d97706' },
];

function getPlaceholder(name) {
  const n = norm(name);
  for (const p of PLACEHOLDER_EMOJIS) {
    if (p.kw.some((k) => n.includes(k))) return p;
  }
  return { emoji: '🔥', from: '#fed7aa', to: '#dc2626' };
}

function ProductImage({ p, variant = 'normal' }) {
  const ph = getPlaceholder(p.name);
  if (p.image_url) {
    return (
      <img
        src={p.image_url}
        alt={p.name}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        loading="lazy"
      />
    );
  }
  return (
    <div
      className="w-full h-full flex items-center justify-center relative overflow-hidden"
      style={{ background: `radial-gradient(circle at 30% 30%, ${ph.from} 0%, ${ph.to} 100%)` }}
    >
      <div className="absolute inset-0 opacity-30 mix-blend-overlay"
           style={{ backgroundImage: 'radial-gradient(circle at 70% 80%, rgba(255,255,255,.6) 0%, transparent 50%)' }} />
      <span className={variant === 'big' ? 'text-[6rem]' : 'text-[4.5rem]'} style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,.25))' }}>
        {ph.emoji}
      </span>
    </div>
  );
}

function ProductCard({ p, isCombo = false, highlight = null }) {
  const [expanded, setExpanded] = useState(false);
  const isPaused = !p.is_active;
  const hasDetails = (p.option_groups && p.option_groups.length > 0) ||
                     (isCombo && (p.combo_items || []).length > 0);

  return (
    <article
      data-testid={`cardapio-v2-product-${p.id}`}
      className={`group relative rounded-3xl overflow-hidden flex flex-col transition-all duration-300 cv2-card
                  ${isPaused ? 'cv2-card-paused' : 'hover:-translate-y-1.5 hover:cv2-card-glow'}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <ProductImage p={p} />
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white via-white/70 to-transparent" />

        {highlight && (
          <div className="absolute top-3 left-3 cv2-chip-top">
            <Crown size={11} className="text-yellow-300" />
            <span>{highlight}</span>
          </div>
        )}
        {isCombo && !highlight && (
          <div className="absolute top-3 left-3 cv2-chip-combo">
            <Layers size={11} className="text-orange-300" /><span>COMBO</span>
          </div>
        )}
        {isPaused && (
          <div className="absolute inset-0 backdrop-blur-[2px] bg-stone-950/45 flex items-center justify-center">
            <div className="cv2-badge-paused"><Pause size={14} strokeWidth={3} /><span>INDISPONÍVEL</span></div>
          </div>
        )}
      </div>

      <div className="relative flex flex-col flex-1 p-5 bg-white/85 backdrop-blur-xl">
        <h3 className="text-[1.35rem] font-black text-stone-900 leading-[1.15] mb-1 tracking-tight">{p.name}</h3>
        {p.description && (
          <p className="text-stone-600 text-[0.92rem] leading-relaxed line-clamp-3 mb-4">{p.description}</p>
        )}

        <div className="mt-auto flex items-end justify-between gap-3 pt-3 border-t border-stone-200/70">
          <div className="leading-none">
            <span className="text-[10px] font-bold text-stone-400 block uppercase tracking-[0.22em] mb-1">a partir de</span>
            <span className="cv2-price">{fmt(p.price)}</span>
          </div>
          {hasDetails && (
            <button
              data-testid={`cardapio-v2-toggle-${p.id}`}
              onClick={() => setExpanded((v) => !v)}
              className="cv2-btn-details"
            >
              {expanded ? <>Ocultar <ChevronUp size={15} /></> : <>Detalhes <ChevronDown size={15} /></>}
            </button>
          )}
        </div>

        {expanded && hasDetails && (
          <div className="mt-4 pt-4 border-t border-dashed border-orange-300 space-y-4">
            {isCombo && (p.combo_items || []).length > 0 && (
              <div>
                <p className="cv2-section-label"><Layers size={13} className="text-orange-500" /> Inclui no combo</p>
                <ul className="space-y-1.5 text-[0.95rem]">
                  {p.combo_items.map((it, i) => (
                    <li key={i} className="flex items-start gap-2 text-stone-700">
                      <span className="text-orange-500 font-black mt-0.5">•</span>
                      <span className="font-semibold">{it.quantity}× {it.child_product_name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {p.option_groups && p.option_groups.map((g) => (
              <div key={g.id}>
                <p className="cv2-section-label">
                  {g.name}
                  {g.is_required && (
                    <span className="ml-1.5 inline-block bg-red-600 text-white text-[9px] px-1.5 py-0.5 rounded font-bold tracking-wider">OBRIGATÓRIO</span>
                  )}
                  <span className="ml-1.5 text-stone-400 font-medium normal-case tracking-normal text-[11px]">
                    {g.min_quantity === g.max_quantity ? `escolha ${g.max_quantity}` : `escolha de ${g.min_quantity} a ${g.max_quantity}`}
                  </span>
                </p>
                <ul className="space-y-1 text-[0.95rem]">
                  {g.options.map((o) => (
                    <li key={o.id} className={`flex items-center justify-between gap-2 ${!o.is_available ? 'opacity-50 line-through' : 'text-stone-700'}`}>
                      <span className="flex items-center gap-1.5">
                        <span className={`font-black ${o.is_removal ? 'text-red-500' : 'text-orange-500'}`}>{o.is_removal ? '−' : '•'}</span>
                        {o.name}
                      </span>
                      {o.price_modifier > 0 && (<span className="font-black text-orange-700 text-sm">+{fmt(o.price_modifier)}</span>)}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}

/* Slider de destaques (top sellers) */
function PromoSlider({ items }) {
  const [idx, setIdx] = useState(0);
  const total = items.length;
  const timerRef = useRef(null);

  const next = useCallback(() => setIdx((i) => (i + 1) % total), [total]);
  const prev = useCallback(() => setIdx((i) => (i - 1 + total) % total), [total]);

  useEffect(() => {
    if (total <= 1) return undefined;
    timerRef.current = setInterval(next, 5500);
    return () => clearInterval(timerRef.current);
  }, [next, total]);

  if (total === 0) return null;
  const current = items[idx];
  const ph = getPlaceholder(current.name);
  const rank = idx + 1;

  return (
    <section className="cv2-promo" data-testid="cardapio-v2-promo-slider">
      <div className="cv2-promo-inner">
        {/* lado esquerdo: visual */}
        <div className="cv2-promo-visual">
          {current.image_url ? (
            <>
              <div
                className="cv2-promo-visual-bg"
                style={{ backgroundImage: `url(${current.image_url})` }}
              />
              <img
                src={current.image_url}
                alt={current.name}
                className="cv2-promo-visual-img"
              />
            </>
          ) : (
            <div className="w-full h-full"
                 style={{ background: `radial-gradient(circle at 30% 30%, ${ph.from} 0%, ${ph.to} 100%)` }}>
              <div className="cv2-promo-visual-emoji">{ph.emoji}</div>
            </div>
          )}
          <div className="absolute inset-0 cv2-promo-overlay pointer-events-none" />
          <div className="absolute top-4 left-4 cv2-promo-rank z-10">#{rank}</div>
        </div>

        {/* lado direito: info */}
        <div className="cv2-promo-content">
          <div className="flex items-center gap-2 mb-3">
            <Crown size={16} className="text-yellow-400" />
            <span className="text-yellow-300 font-black text-[11px] tracking-[0.3em] uppercase">
              Mais Vendidos · Últimos 30 dias
            </span>
          </div>
          <h3 className="cv2-promo-title">{current.name}</h3>
          {current.description && (
            <p className="text-orange-50/85 text-base leading-relaxed line-clamp-3 mb-5 max-w-2xl">
              {current.description}
            </p>
          )}
          <div className="flex items-center gap-6 flex-wrap">
            <div>
              <span className="text-orange-200/70 text-[10px] uppercase tracking-[0.25em] font-bold block mb-0.5">a partir de</span>
              <span className="cv2-promo-price">{fmt(current.price)}</span>
            </div>
            <div className="cv2-promo-pill">
              <TrendingUp size={14} className="text-emerald-300" />
              <span className="font-bold text-sm"><b className="text-emerald-300">{current.sold}</b> vendidos</span>
            </div>
          </div>
        </div>

        {/* controles */}
        {total > 1 && (
          <>
            <button onClick={prev} aria-label="Anterior" className="cv2-promo-arrow cv2-promo-arrow-left" data-testid="cardapio-v2-promo-prev">
              <ChevronLeft size={22} />
            </button>
            <button onClick={next} aria-label="Próximo" className="cv2-promo-arrow cv2-promo-arrow-right" data-testid="cardapio-v2-promo-next">
              <ChevronRight size={22} />
            </button>
            <div className="cv2-promo-dots">
              {items.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  aria-label={`Destaque ${i + 1}`}
                  className={`cv2-promo-dot ${i === idx ? 'cv2-promo-dot-active' : ''}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export default function CardapioV2Page() {
  const [data, setData] = useState({ store_name: STORE_INFO.name, categories: [], products: [], combos: [], top_sellers: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCat, setActiveCat] = useState('all');

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${base}/api/public/cardapio-v2`)
      .then((r) => r.json())
      .then((d) => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const q = norm(search);
  const matches = useCallback((p) => {
    if (!q) return true;
    return norm(p.name).includes(q) || norm(p.description).includes(q);
  }, [q]);

  const grouped = useMemo(() => {
    const map = {};
    for (const cat of data.categories) map[cat.id] = { cat, items: [] };
    for (const p of data.products) {
      if (!matches(p)) continue;
      if (activeCat !== 'all' && activeCat !== '__combos__' && activeCat !== '__top__' && p.category_id !== activeCat) continue;
      if (!map[p.category_id]) continue;
      map[p.category_id].items.push(p);
    }
    return Object.values(map).filter((g) => g.items.length > 0);
  }, [data, matches, activeCat]);

  const filteredCombos = useMemo(() => {
    if (activeCat !== 'all' && activeCat !== '__combos__') return [];
    return data.combos.filter((c) => matches(c));
  }, [data.combos, matches, activeCat]);

  const topSellersFull = useMemo(() => {
    const productsById = new Map(data.products.map((p) => [p.id, p]));
    return (data.top_sellers || []).map((t) => {
      const full = productsById.get(t.id);
      return full ? { ...full, sold: t.sold } : { ...t, option_groups: [] };
    });
  }, [data.top_sellers, data.products]);

  const topSellersFiltered = useMemo(() => {
    if (activeCat !== 'all' && activeCat !== '__top__') return [];
    return topSellersFull.filter((t) => matches(t));
  }, [topSellersFull, matches, activeCat]);

  const totalShown = grouped.reduce((s, g) => s + g.items.length, 0) + filteredCombos.length + topSellersFiltered.length;

  return (
    <div data-testid="cardapio-v2-page" className="cv2-root">
      <style>{`
        .cv2-root { min-height: 100vh; color: #1c1917; background-color: #faf6ef;
          background-image:
            radial-gradient(at 8% 12%, rgba(249,115,22,.10) 0px, transparent 45%),
            radial-gradient(at 92% 8%, rgba(220,38,38,.07) 0px, transparent 45%),
            radial-gradient(at 50% 110%, rgba(180,83,9,.10) 0px, transparent 50%);
          background-attachment: fixed; }
        .cv2-root::before {
          content: ''; position: fixed; inset: 0; pointer-events: none; z-index: 0;
          background-image: linear-gradient(rgba(120,53,15,.035) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(120,53,15,.035) 1px, transparent 1px);
          background-size: 56px 56px;
        }

        /* HERO */
        .cv2-hero { position: relative; overflow: hidden;
          background:
            radial-gradient(ellipse 80% 60% at 20% 0%, rgba(249,115,22,.5) 0%, transparent 60%),
            radial-gradient(ellipse 60% 80% at 90% 100%, rgba(245,158,11,.4) 0%, transparent 60%),
            linear-gradient(135deg, #0c0a09 0%, #2a0d09 30%, #4a1d12 60%, #7c2d12 100%);
        }
        .cv2-hero::after { content: ''; position: absolute; inset: 0; pointer-events: none;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='1.1' numOctaves='2'/><feColorMatrix values='0 0 0 0 .98 0 0 0 0 .9 0 0 0 0 .75 0 0 0 .25 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>");
          opacity: .22; mix-blend-mode: overlay; }
        /* linhas neon decorativas */
        .cv2-hero::before {
          content: ''; position: absolute; inset: 0; pointer-events: none;
          background-image: linear-gradient(180deg, transparent, rgba(251,146,60,.15)),
            radial-gradient(circle at 95% 90%, rgba(251,146,60,.18) 0%, transparent 30%);
        }
        .cv2-hero-grid {
          position: absolute; inset: 0; pointer-events: none; opacity: .07;
          background-image: linear-gradient(rgba(251,146,60,.6) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(251,146,60,.6) 1px, transparent 1px);
          background-size: 40px 40px;
          mask-image: radial-gradient(circle at center, black, transparent 70%);
          -webkit-mask-image: radial-gradient(circle at center, black, transparent 70%);
        }
        .cv2-logo-img {
          height: 96px; width: auto;
          filter: drop-shadow(0 0 24px rgba(251,146,60,.45)) drop-shadow(0 12px 28px rgba(0,0,0,.55));
          animation: cv2-logo-float 4s ease-in-out infinite;
        }
        @media (min-width: 640px) { .cv2-logo-img { height: 128px; } }
        @keyframes cv2-logo-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }

        .cv2-title-grad {
          background: linear-gradient(180deg, #ffffff 0%, #fed7aa 70%, #fb923c 100%);
          -webkit-background-clip: text; background-clip: text; color: transparent;
          text-shadow: 0 0 30px rgba(251,146,60,.2);
        }
        .cv2-glow-flame { filter: drop-shadow(0 0 14px rgba(251,146,60,.7)) drop-shadow(0 0 32px rgba(220,38,38,.4)); }
        @keyframes cv2-pulse-glow {
          0%,100% { filter: drop-shadow(0 0 8px rgba(251,146,60,.7)); }
          50% { filter: drop-shadow(0 0 22px rgba(251,146,60,1)) drop-shadow(0 0 36px rgba(220,38,38,.5)); }
        }
        .cv2-pulse { animation: cv2-pulse-glow 2.6s ease-in-out infinite; }

        /* PROMO SLIDER */
        .cv2-promo {
          position: relative; z-index: 1;
          margin-top: -36px; margin-bottom: 28px;
          padding: 0 16px;
        }
        @media (min-width: 640px) { .cv2-promo { padding: 0 32px; } }
        .cv2-promo-inner {
          position: relative; max-width: 1152px; margin: 0 auto;
          border-radius: 24px; overflow: hidden;
          background: linear-gradient(135deg, #18181b 0%, #44190f 50%, #7c1d1d 100%);
          box-shadow: 0 30px 60px -20px rgba(0,0,0,.6),
                      0 0 0 1px rgba(251,146,60,.25) inset,
                      0 0 80px -10px rgba(251,146,60,.3);
          display: grid; grid-template-columns: 1fr;
        }
        @media (min-width: 768px) {
          .cv2-promo-inner { grid-template-columns: 0.7fr 1.3fr; min-height: 260px; max-height: 280px; }
        }
        .cv2-promo-visual { position: relative; overflow: hidden;
          aspect-ratio: 16/9; max-height: 200px;
          background: #1c1917;
        }
        @media (min-width: 768px) {
          .cv2-promo-visual { aspect-ratio: auto; height: 100%; max-height: 280px; }
        }
        .cv2-promo-visual-bg {
          position: absolute; inset: 0;
          background-size: cover; background-position: center;
          filter: blur(28px) brightness(.95) saturate(1.4);
          transform: scale(1.25);
          z-index: 0;
        }
        .cv2-promo-visual-bg::after {
          content: ''; position: absolute; inset: 0;
          background: linear-gradient(135deg, rgba(124,29,29,.08), rgba(28,25,23,.18));
        }
        .cv2-promo-visual-img {
          position: relative; z-index: 1;
          width: calc(100% - 32px); height: calc(100% - 32px);
          margin: 16px;
          object-fit: contain;
          background: #ffffff;
          border-radius: 18px;
          border: 3px solid rgba(255,255,255,.95);
          box-shadow:
            0 0 0 1px rgba(0,0,0,.18) inset,
            0 18px 36px -10px rgba(0,0,0,.55),
            0 0 28px rgba(251,146,60,.35),
            0 0 0 3px rgba(251,146,60,.25);
        }
        .cv2-promo-visual-emoji {
          position: relative; z-index: 1;
          width: 100%; height: 100%;
          display: flex; align-items: center; justify-content: center;
          font-size: 5.5rem;
          filter: drop-shadow(0 12px 28px rgba(0,0,0,.4)) drop-shadow(0 0 30px rgba(251,146,60,.35));
        }
        @media (min-width: 768px) { .cv2-promo-visual-emoji { font-size: 8rem; } }
        .cv2-promo-overlay {
          background: linear-gradient(to right, transparent 60%, rgba(28,25,23,.25) 100%);
        }
        .cv2-promo-rank {
          background: linear-gradient(135deg, #facc15, #f59e0b);
          color: #1c1917; font-weight: 900; font-size: 16px;
          padding: 5px 12px; border-radius: 10px;
          box-shadow: 0 8px 20px -4px rgba(0,0,0,.5), 0 0 0 2px rgba(255,255,255,.4) inset;
        }
        .cv2-promo-content { padding: 22px 22px 26px; color: #fff; display: flex; flex-direction: column; justify-content: center; }
        @media (min-width: 768px) { .cv2-promo-content { padding: 28px 32px; } }
        .cv2-promo-title {
          font-size: 1.55rem; font-weight: 900; line-height: 1.05;
          letter-spacing: -0.02em; margin-bottom: 8px;
          background: linear-gradient(180deg, #fff 0%, #fed7aa 100%);
          -webkit-background-clip: text; background-clip: text; color: transparent;
        }
        @media (min-width: 768px) { .cv2-promo-title { font-size: 2.1rem; } }
        .cv2-promo-price {
          font-size: 1.9rem; font-weight: 900; letter-spacing: -0.02em; line-height: 1;
          background: linear-gradient(135deg, #fbbf24 0%, #f97316 60%, #dc2626 100%);
          -webkit-background-clip: text; background-clip: text; color: transparent;
          filter: drop-shadow(0 0 18px rgba(251,191,36,.4));
        }
        @media (min-width: 768px) { .cv2-promo-price { font-size: 2.25rem; } }
        .cv2-promo-pill {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 7px 12px; border-radius: 999px;
          background: rgba(255,255,255,.1); backdrop-filter: blur(8px);
          border: 1px solid rgba(255,255,255,.15); color: #f5f5f4;
        }
        .cv2-promo-arrow {
          position: absolute; top: 50%; transform: translateY(-50%);
          width: 38px; height: 38px; border-radius: 999px; display: flex; align-items: center; justify-content: center;
          background: rgba(28,25,23,.7); backdrop-filter: blur(12px);
          border: 1px solid rgba(251,146,60,.4); color: #fed7aa;
          transition: all .2s; z-index: 5; cursor: pointer;
        }
        .cv2-promo-arrow:hover { background: rgba(249,115,22,.85); color: #fff; transform: translateY(-50%) scale(1.06); }
        .cv2-promo-arrow-left { left: 10px; }
        .cv2-promo-arrow-right { right: 10px; }
        .cv2-promo-dots { position: absolute; bottom: 10px; left: 50%; transform: translateX(-50%); display: flex; gap: 6px; z-index: 5; }
        .cv2-promo-dot { width: 7px; height: 7px; border-radius: 999px; background: rgba(255,255,255,.3); transition: all .2s; cursor: pointer; border: 0; padding: 0; }
        .cv2-promo-dot-active { background: #fb923c; width: 22px; box-shadow: 0 0 12px rgba(251,146,60,.6); }

        /* STICKY NAV */
        .cv2-sticky { position: sticky; top: 0; z-index: 30;
          background: rgba(250,246,239,.78); backdrop-filter: blur(20px) saturate(1.4); -webkit-backdrop-filter: blur(20px) saturate(1.4);
          border-bottom: 1px solid rgba(120,53,15,.12); box-shadow: 0 4px 20px -10px rgba(120,53,15,.18); }
        .cv2-input { width: 100%; height: 52px; padding-left: 48px; padding-right: 16px; border-radius: 16px;
          background: rgba(255,255,255,.92); border: 1.5px solid rgba(120,53,15,.15);
          font-size: 1rem; font-weight: 500; color: #1c1917; transition: all .2s; }
        .cv2-input:focus { outline: none; border-color: rgba(249,115,22,.7);
          box-shadow: 0 0 0 4px rgba(249,115,22,.18), 0 8px 20px -8px rgba(249,115,22,.4); background: #fff; }
        .cv2-chip { flex-shrink: 0; padding: 8px 16px; border-radius: 999px;
          font-size: 0.875rem; font-weight: 700; letter-spacing: .01em;
          border: 1.5px solid rgba(120,53,15,.16); background: rgba(255,255,255,.85); color: #44403c;
          white-space: nowrap; transition: background .2s, color .2s, transform .2s, border-color .2s;
          cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
          outline: none !important; -webkit-tap-highlight-color: transparent;
          appearance: none; -webkit-appearance: none;
        }
        .cv2-chip:focus, .cv2-chip:focus-visible, .cv2-chip:active {
          outline: none !important;
          box-shadow: none !important;
        }
        .cv2-chip:hover { border-color: rgba(249,115,22,.6); color: #c2410c; transform: translateY(-1px); }
        .cv2-chip-active { background: linear-gradient(135deg, #ea580c 0%, #dc2626 100%); color: #fff; border-color: transparent;
          box-shadow: 0 8px 20px -6px rgba(220,38,38,.55); }
        .cv2-chip-active:hover { color: #fff; }
        .cv2-chip-top-bg { background: linear-gradient(135deg, #facc15, #f59e0b); color: #1c1917; border-color: transparent;
          box-shadow: 0 8px 20px -6px rgba(234,179,8,.55); }
        .cv2-chip-top-bg:hover { color: #1c1917; }

        /* Scroll horizontal das categorias */
        .cv2-chips-wrap {
          position: relative;
        }
        .cv2-chips-scroll {
          display: flex;
          gap: 8px;
          overflow-x: auto;
          overflow-y: hidden;
          -webkit-overflow-scrolling: touch;
          touch-action: pan-x;
          scroll-snap-type: x proximity;
          padding: 4px 32px 8px 4px;
          margin: 0 -4px;
        }
        .cv2-chips-scroll > * { scroll-snap-align: start; }
        .cv2-chips-wrap::after {
          content: ''; position: absolute; top: 0; right: 0; bottom: 4px; width: 40px;
          pointer-events: none;
          background: linear-gradient(90deg, rgba(250,246,239,0), rgba(250,246,239,.95) 75%);
        }

        /* SECTIONS */
        .cv2-section-title { color: #1c1917; font-size: 1.5rem; font-weight: 900;
          letter-spacing: -0.02em; line-height: 1; text-transform: uppercase; display: flex; align-items: center; gap: 12px; }
        @media(min-width: 640px) { .cv2-section-title { font-size: 1.85rem; } }
        .cv2-section-accent { width: 48px; height: 6px; border-radius: 999px;
          background: linear-gradient(90deg, #ea580c, #dc2626);
          box-shadow: 0 2px 12px rgba(220,38,38,.4); }
        .cv2-section-accent-gold { background: linear-gradient(90deg, #facc15, #f59e0b); box-shadow: 0 2px 12px rgba(234,179,8,.5); }
        .cv2-section-count { color: #78716c; font-size: 0.82rem; font-weight: 600; letter-spacing: .02em; }

        /* CARD */
        .cv2-card { background: rgba(255,255,255,.7); border: 1.5px solid rgba(120,53,15,.10);
          backdrop-filter: blur(12px);
          box-shadow: 0 4px 12px -4px rgba(120,53,15,.12), 0 0 0 1px rgba(255,255,255,.4) inset; }
        .cv2-card-glow { box-shadow: 0 24px 48px -16px rgba(249,115,22,.35), 0 12px 24px -10px rgba(220,38,38,.18),
          0 0 0 1px rgba(255,255,255,.5) inset !important; }
        .cv2-card-paused { background: rgba(245,245,244,.6); border-color: rgba(120,53,15,.06); filter: grayscale(35%); }
        .cv2-chip-combo { display: inline-flex; align-items: center; gap: 5px; padding: 5px 10px;
          background: linear-gradient(135deg, #18181b 0%, #292524 100%); color: #fff;
          font-size: 10px; font-weight: 800; letter-spacing: .14em; border-radius: 999px;
          box-shadow: 0 8px 20px -6px rgba(0,0,0,.5), 0 0 0 1px rgba(249,115,22,.4); }
        .cv2-chip-top { display: inline-flex; align-items: center; gap: 5px; padding: 5px 10px;
          background: linear-gradient(135deg, #facc15 0%, #f97316 100%); color: #1c1917;
          font-size: 10px; font-weight: 900; letter-spacing: .14em; border-radius: 999px;
          box-shadow: 0 8px 20px -6px rgba(234,179,8,.6), 0 0 0 1px rgba(255,255,255,.4) inset; }
        .cv2-badge-paused { display: inline-flex; align-items: center; gap: 6px; padding: 8px 20px;
          background: linear-gradient(135deg, #991b1b 0%, #7f1d1d 100%); color: #fff;
          font-weight: 900; font-size: 13px; letter-spacing: .22em; border-radius: 999px;
          transform: rotate(-6deg);
          box-shadow: 0 16px 32px -8px rgba(127,29,29,.6), 0 0 0 2px rgba(255,255,255,.95) inset, 0 0 0 1px rgba(127,29,29,.4); }
        .cv2-price { background: linear-gradient(135deg, #ea580c 0%, #dc2626 100%);
          -webkit-background-clip: text; background-clip: text; color: transparent;
          font-size: 1.75rem; font-weight: 900; letter-spacing: -0.02em; display: block; }
        @media(min-width: 640px) { .cv2-price { font-size: 1.85rem; } }
        .cv2-btn-details { display: inline-flex; align-items: center; gap: 4px; padding: 8px 14px;
          border-radius: 12px; font-size: 13px; font-weight: 800; letter-spacing: .02em;
          color: #c2410c; background: rgba(254,243,228,.7); border: 1.5px solid rgba(249,115,22,.35);
          backdrop-filter: blur(8px); transition: all .2s; cursor: pointer; }
        .cv2-btn-details:hover { background: linear-gradient(135deg, #ea580c, #dc2626); color: #fff; border-color: transparent;
          box-shadow: 0 8px 20px -6px rgba(220,38,38,.55); transform: translateY(-1px); }
        .cv2-section-label { font-size: 11px; font-weight: 900; letter-spacing: .16em; text-transform: uppercase;
          color: #44403c; margin-bottom: 6px; display: inline-flex; align-items: center; flex-wrap: wrap; gap: 4px; }

        /* PAYMENTS */
        .cv2-pay-bar { position: fixed; bottom: 0; left: 0; right: 0; z-index: 40;
          background: rgba(28,25,23,.95); backdrop-filter: blur(24px) saturate(1.5);
          border-top: 1px solid rgba(251,146,60,.2); box-shadow: 0 -10px 30px -8px rgba(0,0,0,.45); }
        .cv2-pay-chip { display: inline-flex; align-items: center; gap: 6px; padding: 7px 12px; color: #fff;
          font-size: 12px; font-weight: 800; letter-spacing: .02em; border-radius: 10px;
          box-shadow: 0 6px 14px -4px rgba(0,0,0,.5), 0 0 0 1px rgba(255,255,255,.15) inset; backdrop-filter: blur(8px); }

        /* INFO */
        .cv2-info { background: linear-gradient(180deg, transparent 0%, rgba(28,25,23,.04) 100%); border-top: 1px solid rgba(120,53,15,.15); }
        .cv2-info-card { background: rgba(255,255,255,.6); backdrop-filter: blur(12px); border: 1px solid rgba(120,53,15,.10); border-radius: 20px; padding: 20px; }
        .cv2-info-row { display: flex; align-items: flex-start; gap: 12px; color: #44403c; font-size: 0.92rem; line-height: 1.5; }
        .cv2-info-row svg { margin-top: 2px; flex-shrink: 0; color: #ea580c; }
        .cv2-info-title { font-weight: 900; font-size: 11px; letter-spacing: .2em; text-transform: uppercase;
          color: #78716c; margin-bottom: 10px; display: flex; align-items: center; gap: 6px; }

        .cv2-scrollx { scrollbar-width: none; -ms-overflow-style: none; }
        .cv2-scrollx::-webkit-scrollbar { display: none; }
        .cv2-content { position: relative; z-index: 1; }
      `}</style>

      <div className="cv2-content">
        {/* HERO */}
        <header className="cv2-hero">
          <div className="cv2-hero-grid" />
          <div className="relative z-10 px-4 sm:px-8 pt-10 sm:pt-14 pb-16 sm:pb-24">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center justify-between gap-6 flex-wrap">
                <div className="flex items-center gap-5 sm:gap-6">
                  <img
                    src="/logo.png"
                    alt="Hit Grill"
                    className="cv2-logo-img"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                  <div>
                    <p className="text-orange-300 text-[10px] sm:text-[11px] font-bold tracking-[0.4em] uppercase flex items-center gap-1.5">
                      <Sparkles size={12} className="cv2-pulse" /> Cardápio Digital
                    </p>
                    <h1 className="cv2-title-grad text-4xl sm:text-6xl font-black leading-[0.95] flex items-center gap-3 mt-1.5 tracking-tight">
                      {data.store_name || STORE_INFO.name}
                      <Flame size={36} className="text-orange-400 cv2-glow-flame cv2-pulse" />
                    </h1>
                    <p className="text-orange-100/90 text-sm sm:text-base font-semibold mt-2 tracking-wide">
                      {STORE_INFO.tagline} · Sabor de churrasco, do tradicional ao premium.
                    </p>
                  </div>
                </div>

                <div className="hidden lg:flex flex-col gap-2 text-orange-50/90 text-sm">
                  <a href={`https://wa.me/${STORE_INFO.phone_digits}`} target="_blank" rel="noreferrer"
                     className="flex items-center gap-2 hover:text-white transition-colors font-bold">
                    <Phone size={16} className="text-orange-300" /> {STORE_INFO.phone}
                  </a>
                  <span className="flex items-center gap-2 text-orange-100/75 text-[13px]">
                    <MapPin size={14} className="text-orange-300" /> {STORE_INFO.address_line1}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* PROMO SLIDER (top sellers) */}
        {!loading && topSellersFull.length > 0 && <PromoSlider items={topSellersFull.slice(0, 5)} />}

        {/* STICKY NAV */}
        <div className="cv2-sticky">
          <div className="max-w-6xl mx-auto px-4 sm:px-8 py-3.5 space-y-3">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={20} />
              <input
                data-testid="cardapio-v2-search-input"
                type="search"
                placeholder="Buscar produto pelo nome…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="cv2-input"
              />
            </div>
            <div className="cv2-chips-wrap">
              <div className="cv2-chips-scroll cv2-scrollx">
                <button
                  data-testid="cardapio-v2-cat-all"
                  onClick={() => setActiveCat('all')}
                  className={`cv2-chip ${activeCat === 'all' ? 'cv2-chip-active' : ''}`}
                >Tudo</button>
                {topSellersFull.length > 0 && (
                  <button
                    data-testid="cardapio-v2-cat-top"
                    onClick={() => setActiveCat('__top__')}
                    className={`cv2-chip ${activeCat === '__top__' ? 'cv2-chip-top-bg' : ''}`}
                  ><Crown size={13} /> Top Vendidos</button>
                )}
                {data.combos.length > 0 && (
                  <button
                    data-testid="cardapio-v2-cat-combos"
                    onClick={() => setActiveCat('__combos__')}
                    className={`cv2-chip ${activeCat === '__combos__' ? 'cv2-chip-active' : ''}`}
                  ><Layers size={13} /> Combos</button>
                )}
                {data.categories.map((c) => (
                  <button
                    key={c.id}
                    data-testid={`cardapio-v2-cat-${c.id}`}
                    onClick={() => setActiveCat(c.id)}
                    className={`cv2-chip ${activeCat === c.id ? 'cv2-chip-active' : ''}`}
                  >{c.name}</button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* LISTA */}
        <main className="max-w-6xl mx-auto px-4 sm:px-8 py-8 pb-32">
          {loading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-80 rounded-3xl bg-white/50 animate-pulse" />
              ))}
            </div>
          )}

          {!loading && totalShown === 0 && (
            <div className="text-center py-20" data-testid="cardapio-v2-empty">
              <Flame size={64} className="mx-auto text-orange-300 mb-4 cv2-pulse" strokeWidth={1.5} />
              <p className="text-stone-700 text-xl font-bold">Nada encontrado.</p>
              <p className="text-stone-500 text-base mt-2">
                {search ? 'Tente outro nome ou limpe a busca.' : 'Selecione outra categoria.'}
              </p>
            </div>
          )}

          {/* TOP VENDIDOS - sempre exibe se 'all' ou '__top__' */}
          {!loading && topSellersFiltered.length > 0 && (
            <section className="mb-14" data-testid="cardapio-v2-section-top">
              <div className="flex items-center gap-3 mb-7">
                <div className="cv2-section-accent cv2-section-accent-gold" />
                <h2 className="cv2-section-title">
                  <Crown size={26} className="text-yellow-500" /> Top Vendidos
                </h2>
                <span className="cv2-section-count">· últimos 30 dias</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {topSellersFiltered.map((p, i) => (
                  <ProductCard key={p.id} p={p} highlight={`#${i + 1} MAIS VENDIDO`} />
                ))}
              </div>
            </section>
          )}

          {!loading && filteredCombos.length > 0 && (
            <section className="mb-14">
              <div className="flex items-center gap-3 mb-7">
                <div className="cv2-section-accent" />
                <h2 className="cv2-section-title"><Layers size={24} className="text-orange-600" /> Combos</h2>
                <span className="cv2-section-count">· {filteredCombos.length}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCombos.map((c) => <ProductCard key={c.id} p={c} isCombo />)}
              </div>
            </section>
          )}

          {!loading && grouped.map(({ cat, items }) => (
            <section key={cat.id} className="mb-14" data-testid={`cardapio-v2-section-${cat.id}`}>
              <div className="flex items-center gap-3 mb-7">
                <div className="cv2-section-accent" />
                <h2 className="cv2-section-title">{cat.name}</h2>
                <span className="cv2-section-count">· {items.length} {items.length === 1 ? 'item' : 'itens'}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {items.map((p) => <ProductCard key={p.id} p={p} />)}
              </div>
            </section>
          ))}

          {/* INFO EMPRESA */}
          <section className="cv2-info mt-16 pt-10 pb-8" data-testid="cardapio-v2-store-info">
            <div className="grid sm:grid-cols-3 gap-5">
              <div className="cv2-info-card">
                <p className="cv2-info-title"><Building2 size={13} /> Empresa</p>
                <div className="cv2-info-row">
                  <Building2 size={16} />
                  <div>
                    <p className="font-black text-stone-900">{STORE_INFO.name}</p>
                    <p className="text-xs text-stone-500 font-medium">CNPJ {STORE_INFO.cnpj}</p>
                  </div>
                </div>
              </div>
              <div className="cv2-info-card">
                <p className="cv2-info-title"><MapPin size={13} /> Endereço</p>
                <div className="cv2-info-row">
                  <MapPin size={16} />
                  <div>
                    <p className="font-bold text-stone-800">{STORE_INFO.address_line1}</p>
                    <p>{STORE_INFO.address_line2}</p>
                    <p className="text-xs text-stone-500 font-medium mt-0.5">CEP {STORE_INFO.cep}</p>
                  </div>
                </div>
              </div>
              <div className="cv2-info-card">
                <p className="cv2-info-title"><Phone size={13} /> Contato</p>
                <div className="cv2-info-row">
                  <Phone size={16} />
                  <div>
                    <a href={`https://wa.me/${STORE_INFO.phone_digits}`} target="_blank" rel="noreferrer"
                       data-testid="cardapio-v2-whatsapp-link"
                       className="font-bold text-stone-800 hover:text-orange-700 transition-colors block">
                      {STORE_INFO.phone}
                    </a>
                    <p className="text-xs text-stone-500 font-medium">Toque para chamar no WhatsApp</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-between flex-wrap gap-3 text-xs text-stone-500 px-1">
              <p className="font-medium">© {new Date().getFullYear()} {STORE_INFO.name} · Todos os direitos reservados.</p>
              <a href="/privacidade" data-testid="cardapio-v2-lgpd-link"
                 className="inline-flex items-center gap-1.5 font-bold text-stone-600 hover:text-orange-700 transition-colors">
                <FileText size={13} /> Política de Privacidade e LGPD
              </a>
            </div>
          </section>
        </main>

        {/* PAGAMENTOS FIXED */}
        <footer data-testid="cardapio-v2-footer-payments" className="cv2-pay-bar">
          <div className="max-w-6xl mx-auto px-3 sm:px-8 py-3">
            <div className="flex items-center gap-3 flex-wrap justify-center sm:justify-between">
              <p className="text-orange-300 font-black text-[10px] sm:text-xs uppercase tracking-[0.3em] hidden md:block">Aceitamos</p>
              <div className="flex flex-wrap gap-1.5 sm:gap-2 justify-center">
                {PAYMENT_METHODS.map((pm) => (
                  <div key={pm.id} data-testid={`cardapio-v2-pm-${pm.id}`} className="cv2-pay-chip"
                       style={{ background: `linear-gradient(135deg, ${pm.color}, ${pm.color}cc)` }}>
                    <pm.icon size={13} strokeWidth={2.6} />{pm.label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
